import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { get, isEmpty } from "lodash";
import { useNavigate, useParams } from "helpers/tixxt-router";

import PageTitle from "components/layout/PageTitle";
import GroupForm from "./form";
import { groupCategorySelector } from "selectors/environment";

import { fetchGroupDefaults, createGroup } from "./api";
import { useQuery, useMutation } from "react-query";
import { useCreateItem, useLoadApp } from "../appCreator/api";
import { Group } from "../../@types";
import Loading from "components/shared/Loading";

export default function NewGroup() {
  const navigate = useNavigate();
  const networkId = Preload.current_network.id;
  const { groupCategorySlug } = useParams();
  const category = useSelector((state) =>
    groupCategorySelector(state, groupCategorySlug),
  );

  const { mutate: createItem } = useCreateItem({
    onSuccess: (item, variables) =>
      onCreateGroup({
        body: { group: { ...variables.group, profile_id: item?.id } },
      }),
  });

  const { data: groupDefaults, isLoading: loading } = useQuery(
    ["groupDefaults", groupCategorySlug],
    () => fetchGroupDefaults(groupCategorySlug),
  );
  const externalAppId = `network_${networkId}:group_category_${category?.id}:group_profile`;
  const { data: app, isLoading: isAppLoading } = useLoadApp({
    externalAppId,
  });

  const { mutate: onCreateGroup } = useMutation(createGroup, {
    onSuccess: (data, variables) => {
      toastr.success(
        I18n.t("js.groups.create.success", {
          group: get(variables, ["body", "group", "name"]),
        }),
      );
      navigate(`/groups/${data.groupSlug}/activitystreams/${data.groupSlug}`);
    },
    onError: () => {
      toastr.error(
        I18n.t("js.groups.create.failure", {
          group: category?.name,
        }),
      );
    },
  });

  const onSubmit = async (
    values: Group & { profile: { [key: string]: string | boolean } },
    { externalAppId }: { externalAppId?: string },
  ) => {
    const { profile, ...group } = values;

    createItem({ externalAppId, body: { values: profile }, group });
  };

  if (loading) return <Loading />;

  return (
    <Fragment>
      <PageTitle
        title={category?.name || ""}
        subtitle={I18n.t("js.groups.new.create")}
      />
      {!isEmpty(groupDefaults) && !loading && (
        <GroupForm
          app={app}
          group={groupDefaults}
          onSubmit={onSubmit}
          isAppLoading={isAppLoading}
        />
      )}
    </Fragment>
  );
}
